import { useEffect } from 'react';

import type { BrandPickerMode } from './useGlobalState';

import { useGlobalState } from './useGlobalState';

const defaultPlaceholderText = 'Enter text here';

interface HeaderControls {
    isSearchBarVisible?: boolean;
    isResultCountVisible?: boolean;
    brandPickerMode?: BrandPickerMode;
    searchPlaceholder?: string;
}

/**
 * Syncs the umbrella header controls (search bar, result count, brand picker, placeholder)
 * to global state for the page that renders it. The Toolbar reads these flags and is
 * otherwise route-agnostic, so each page declares the header chrome it needs.
 *
 * Every top-level page must call this hook, since there is no unmount reset: a page that
 * skips it would inherit the previous page's header. Pages that want the hidden baseline
 * call it with no arguments.
 */
const useGlobalHeaderState = ({
    isSearchBarVisible = false,
    isResultCountVisible = false,
    brandPickerMode = 'hidden',
    searchPlaceholder = defaultPlaceholderText,
}: HeaderControls = {}): void => {
    const [, setSearchBarVisible] = useGlobalState('isSearchBarVisible');
    const [, setResultCountVisible] = useGlobalState('isResultCountVisible');
    const [, setBrandPickerMode] = useGlobalState('brandPickerMode');
    const [, setSearchPlaceholder] = useGlobalState('searchPlaceholder');

    // Write values on mount and whenever they change. Each page owns its header, so the next
    // page's call overwrites these values. Pages that opt out are expected to call with defaults.
    useEffect(() => {
        setSearchBarVisible(isSearchBarVisible);
        setResultCountVisible(isResultCountVisible);
        setBrandPickerMode(brandPickerMode);
        setSearchPlaceholder(searchPlaceholder);
    }, [
        isSearchBarVisible,
        isResultCountVisible,
        brandPickerMode,
        searchPlaceholder,
        setSearchBarVisible,
        setResultCountVisible,
        setBrandPickerMode,
        setSearchPlaceholder,
    ]);
};

export { useGlobalHeaderState };
export type { HeaderControls };
